import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import BackOrHomeButton from '@/components/BackOrHomeButton';
import SiteDisclaimer from '@/components/SiteDisclaimer';
import StockModal from '@/components/StockModal';
import CompareModal from '@/components/CompareModal';
import LoginModal from '@/components/LoginModal';
import AbbrTip from '@/components/AbbrTip';
import AbbrText from '@/components/AbbrText';
import { fetchStockWithFallback } from '@/lib/stockFetch';
import { useMarketTheme } from '@/hooks/useMarketTheme';
import { useAuth } from '@/contexts/AuthContext';
import { Crown, Trophy, Medal, TrendingDown, Droplets, Percent, Gauge, AlertTriangle, ExternalLink, HelpCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_META = {
  king: {
    title: 'Dividend Kings Undervaluation Heatmap',
    seo: 'Dividend Kings Undervaluation Heatmap — 50+ Year Dividend Growers Ranked by Value | TwylightCrow',
    desc: 'Live quantitative heatmap of every Dividend King (50+ years of increases): yield vs 3-year history, beta-adjusted sector dislocation, and free-cash-flow dividend coverage blended into one undervaluation score.',
    icon: Crown,
    label: 'Kings',
    challenge: '/kings-challenge',
  },
  aristocrat: {
    title: 'Dividend Aristocrats Undervaluation Heatmap',
    seo: 'Dividend Aristocrats Undervaluation Heatmap — 25+ Year Dividend Growers Ranked by Value | TwylightCrow',
    desc: 'Live quantitative heatmap of the Dividend Aristocrats (25+ years of increases): historical yield bands, sector-relative Z-scores, and FCF payout safety in one color-coded undervaluation view.',
    icon: Trophy,
    label: 'Aristocrats',
    challenge: '/aristocrats-challenge',
  },
  achiever: {
    title: 'Dividend Achievers Undervaluation Heatmap',
    seo: 'Dividend Achievers Undervaluation Heatmap — 10+ Year Dividend Growers Ranked by Value | TwylightCrow',
    desc: 'Live quantitative heatmap of the Dividend Achievers (10+ years of increases): yield vs history, beta-adjusted sector dislocation, and cash-flow coverage combined into one undervaluation signal.',
    icon: Medal,
    label: 'Achievers',
    challenge: '/achievers-challenge',
  },
};

const METRICS = [
  {
    key: 'composite',
    label: 'Composite',
    blurb: 'Blend: 40% yield band + 35% sector dislocation + 25% FCF safety. Green only when structurally cheap AND technically oversold.',
    sortLabel: 'Chowder Score, descending',
    // Composite view keeps the chowder-desc order (best "yield + growth"
    // quality overlay first). Sort is stable for null chowders.
    sortFn: (a, b) => (a.chowder == null) - (b.chowder == null) || (b.chowder ?? 0) - (a.chowder ?? 0),
  },
  {
    key: 'ytb',
    label: 'Yield vs History',
    blurb: 'Structural value: current TTM yield vs its own 3-year mean. Slow-moving, ignores single-day panics. Distressed yields (\u22652.5\u00D7 own history) marked with a warning border.',
    sortLabel: 'Yield vs 3-Yr History, descending',
    sortFn: (a, b) => (a.ytb == null) - (b.ytb == null) || (b.ytb ?? -Infinity) - (a.ytb ?? -Infinity),
  },
  {
    key: 'z',
    label: 'Sector Z-Score',
    blurb: 'Tactical timing: beta-adjusted 30-day dislocation vs the stock\u2019s SPDR sector ETF. Below \u22121\u03C3 = statistically stretched to the downside.',
    sortLabel: '30-Day Sector Dislocation, most oversold first',
    // Z-score: most negative (oversold) first.
    sortFn: (a, b) => (a.z30 == null) - (b.z30 == null) || (a.z30 ?? Infinity) - (b.z30 ?? Infinity),
  },
];

// Chowder Rule (aligned with /glossary#entry-chowder-score):
//   yield < 3%  → threshold 12 (low-yield growth compounders)
//   yield >= 3% → threshold  8 (typical mid-yield / REIT / high-yield)
function chowderVerdict(chowder, yieldPct) {
  if (chowder == null || yieldPct == null) return null;
  const threshold = yieldPct < 3 ? 12 : 8;
  return { pass: chowder >= threshold, threshold };
}

// Diverging color: deep red (trap) → slate gray (fair) → emerald (undervalued)
export function heatColor(p) {
  const stops = [[165, 0, 38], [100, 108, 122], [0, 104, 55]];
  const t = Math.max(0, Math.min(1, p ?? 0.5));
  const [a, b, f] = t <= 0.5 ? [stops[0], stops[1], t / 0.5] : [stops[1], stops[2], (t - 0.5) / 0.5];
  const mix = a.map((v, i) => Math.round(v + (b[i] - v) * f));
  return `rgb(${mix[0]},${mix[1]},${mix[2]})`;
}

const fmtPct = (v, dp = 1) => (v == null ? '—' : `${(v * 100).toFixed(dp)}%`);

function ScoreRow({ icon: Icon, label, value, note, tone, highlight }) {
  return (
    <div
      className={`flex items-start gap-3 py-2 border-b border-border/40 last:border-0 ${
        highlight ? 'bg-primary/5 border-l-2 border-l-primary -mx-4 pl-4 pr-4' : ''
      }`}
      data-testid={highlight ? 'heatmap-modal-row-active' : undefined}
    >
      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${tone || 'text-muted-foreground'}`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className={`text-xs font-semibold uppercase tracking-wide ${highlight ? 'text-primary' : 'text-muted-foreground'}`}>
            {label}{highlight && <span className="ml-1.5 text-[9px] font-black">{'\u2190 ACTIVE'}</span>}
          </span>
          <span className="text-sm font-bold tabular-nums">{value}</span>
        </div>
        {note && <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{note}</p>}
      </div>
    </div>
  );
}

function TileModal({ tile, metric = 'composite', onClose, onOpenStockModal }) {
  // Always mounted; visibility driven by the `open` prop so Radix runs
  // its full close cleanup (abrupt conditional unmount leaves
  // `pointer-events: none` stuck on <body>, freezing the StockModal
  // opened right after from the "Open in Stock Modal" button).
  const comp = tile?.composite ?? 0;
  const verdict = comp > 0.3 ? 'Historically undervalued + oversold' : comp < -0.3 ? 'Overvalued / value-trap territory' : 'Near fair value';
  const zNote = !tile || tile.z30 == null ? 'Insufficient price history'
    : `${Math.abs(tile.z30).toFixed(1)}\u03C3 ${tile.z30 < 0 ? 'OVERSOLD' : 'overbought'} vs ${tile.sector_etf} over ~30 days (\u03B2 ${tile.beta_used ?? '\u2014'})`;
  const fcfNote = !tile || tile.fcf_payout == null ? 'FCF data unavailable'
    : tile.fcf_payout >= 1 ? 'Dividend NOT covered by free cash flow' : 'Dividend covered by free cash flow';
  // Header band mirrors whichever metric switch is currently active on
  // the parent page — otherwise clicking a green YTB tile would open a
  // red composite-colored modal (P107 bug fix, Feb 2026).
  const bandColorPos = tile?.color?.[metric] ?? tile?.color?.composite ?? 0.5;
  return (
    <Dialog open={!!tile} onOpenChange={(o) => { if (!o) onClose(); }}>
      {tile && (
      <DialogContent
        className="max-w-sm p-0 overflow-hidden [&>[data-testid=dialog-close-btn]]:text-white [&>[data-testid=dialog-close-btn]]:bg-black/35 [&>[data-testid=dialog-close-btn]]:border-white/80 [&>[data-testid=dialog-close-btn]]:hover:bg-red-600 [&>[data-testid=dialog-close-btn]]:hover:text-white [&>[data-testid=dialog-close-btn]]:hover:border-white"
        data-testid="heatmap-tile-modal"
      >
        {/* Oct 2026 — min-w-0/overflow-hidden: a nowrap name (MKC "McCormick &
            Company, Incorporated Non-VTG CS") set the grid item's min-content
            width and pushed the whole card past its right edge. pr-14 keeps the
            streak pill clear of the (now white-on-dark) close button. */}
        <div className="px-4 py-3 pr-14 min-w-0 overflow-hidden" style={{ backgroundColor: heatColor(bandColorPos) }}>
          <DialogTitle className="text-white text-lg font-black flex items-center justify-between gap-2 min-w-0">
            <span>{tile.ticker}</span>
            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-black/25 whitespace-nowrap">{tile.tier?.toUpperCase()}{tile.streak_years ? ` \u00B7 ${tile.streak_years}-YR STREAK` : ''}</span>
          </DialogTitle>
          <p className="text-white/85 text-xs mt-0.5 truncate" title={`${tile.name || ''}${tile.sector ? ` \u00B7 ${tile.sector}` : ''}`} data-testid="heatmap-modal-name">{tile.name} {tile.sector ? `\u00B7 ${tile.sector}` : ''}</p>
        </div>
        <div className="px-4 py-2 min-w-0">
          <ScoreRow icon={Gauge} label="Composite Master Score" value={comp.toFixed(2)} note={verdict}
            tone={comp > 0.3 ? 'text-emerald-600' : comp < -0.3 ? 'text-red-600' : 'text-muted-foreground'}
            highlight={metric === 'composite'} />
          <ScoreRow icon={Percent} label="Yield vs 3-Yr History" value={tile.ytb == null ? '\u2014' : `${tile.ytb > 0 ? '+' : ''}${fmtPct(tile.ytb)}`}
            note={tile.ytb == null ? 'Yield history too young' : tile.ytb > 0 ? 'Yield ABOVE its own 3-year mean (historically cheap)' : 'Yield below its own 3-year mean'}
            highlight={metric === 'ytb'} />
          <ScoreRow icon={TrendingDown} label="Beta-Adj. Sector Z-Score" value={tile.z30 == null ? '\u2014' : tile.z30.toFixed(2)} note={zNote}
            tone={tile.z30 != null && tile.z30 < -1 ? 'text-emerald-600' : undefined}
            highlight={metric === 'z'} />
          <ScoreRow icon={Droplets} label="FCF Payout Safety" value={tile.fcf_payout == null ? 'n/a' : fmtPct(tile.fcf_payout, 0)} note={fcfNote}
            tone={tile.fcf_payout != null && tile.fcf_payout >= 1 ? 'text-red-600' : undefined} />
          <ScoreRow icon={Gauge} label="Chowder Number" value={tile.chowder == null ? '\u2014' : tile.chowder.toFixed(1)}
            note={`Yield ${tile.dividend_yield?.toFixed?.(2) ?? '\u2014'}% + 5-yr div CAGR ${tile.cagr_5y?.toFixed?.(2) ?? '\u2014'}%`} />
          {(tile.flags || []).length > 0 && (
            <div className="flex items-start gap-2 py-2 text-[11px] text-amber-600 dark:text-amber-400" data-testid="heatmap-modal-flags">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                {tile.flags.includes('uncovered_dividend') && 'CRITICAL: dividend uncovered by FCF (forced trap). '}
                {tile.flags.includes('distressed_yield') && 'CRITICAL: yield \u22652.5\u00D7 its 3-yr mean — distress signal, not a bargain (forced trap). '}
                {tile.flags.includes('young_yield_history') && 'Yield history under 2 years — yield-band score neutral. '}
                {tile.flags.includes('fcf_na') && 'FCF unavailable — safety score neutral. '}
                {tile.flags.includes('negative_fcf') && 'Negative free cash flow. '}
                {tile.flags.includes('no_cagr_5y') && '5-yr dividend CAGR unavailable. '}
              </span>
            </div>
          )}
          <Link to={`/stock/${tile.ticker}`} className="flex items-center justify-center gap-1.5 w-full py-2 mt-2 mb-1.5 rounded-lg border text-xs font-bold hover:bg-muted/50 transition-colors" data-testid="heatmap-modal-full-stock-link">
            OPEN {tile.ticker} IN STOCK PAGE <ExternalLink className="w-3 h-3" />
          </Link>
          <button type="button" onClick={() => onOpenStockModal?.(tile.ticker)}
            className="flex items-center justify-center gap-1.5 w-full py-2 mb-2 rounded-lg border text-xs font-bold hover:bg-muted/50 transition-colors"
            data-testid="heatmap-modal-open-stock-modal-btn">
            OPEN {tile.ticker} IN STOCK MODAL <Gauge className="w-3 h-3" />
          </button>
          <p className="text-[10px] text-muted-foreground text-center pb-2">Quantitative screen only — not investment advice.</p>
        </div>
      </DialogContent>
      )}
    </Dialog>
  );
}

export default function HeatmapPage({ tier = 'king' }) {
  const meta = TIER_META[tier] || TIER_META.king;
  const [tiles, setTiles] = useState([]);
  const [computedAt, setComputedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState('composite');
  const [selected, setSelected] = useState(null);
  // Full StockModal (opened from the tile modal's second button)
  const { isDark } = useMarketTheme();
  const { user } = useAuth();
  const [stockData, setStockData] = useState(null);
  const [stockModalOpen, setStockModalOpen] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareTicker, setCompareTicker] = useState(null);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [watchlist, setWatchlist] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) { setWatchlist([]); return; }
    axios.get(`${API}/portfolio`, { withCredentials: true })
      .then(({ data }) => setWatchlist(data.map(p => p.ticker)))
      .catch(() => setWatchlist([]));
  }, [user]);

  const toggleWatchlist = async (ticker) => {
    if (!user) { setLoginModalOpen(true); return; }
    const inList = watchlist.includes(ticker);
    try {
      if (inList) {
        await axios.delete(`${API}/portfolio/${ticker}`, { withCredentials: true });
        setWatchlist(w => w.filter(t => t !== ticker));
      } else {
        await axios.post(`${API}/portfolio`, { ticker }, { withCredentials: true });
        setWatchlist(w => [...w, ticker]);
      }
    } catch { /* noop */ }
  };

  const openStockModal = useCallback(async (ticker) => {
    setSelected(null);
    setLoadingDetail(true);
    setStockModalOpen(true);
    try {
      const data = await fetchStockWithFallback(ticker, {
        timeoutMs: 3500,
        onFresh: (fresh) => setStockData(fresh),
      });
      setStockData(data);
    } catch {
      setStockData(null);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    document.title = meta.seo;
    let el = document.querySelector('meta[name="description"]');
    if (!el) { el = document.createElement('meta'); el.name = 'description'; document.head.appendChild(el); }
    el.content = meta.desc;
  }, [meta]);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/heatmap/${tier}`)
      .then(({ data }) => {
        setTiles(data.tiles || []);
        setComputedAt(data.computed_at);
        const focus = new URLSearchParams(location.search).get('focus');
        if (focus) {
          const hit = (data.tiles || []).find(t => t.ticker === focus.toUpperCase());
          if (hit) openTile(hit.ticker);
        }
      })
      .catch(() => setTiles([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tier]);

  const openTile = useCallback((ticker) => {
    axios.get(`${API}/heatmap/${tier}/${ticker}`)
      .then(({ data }) => setSelected(data))
      .catch(() => {});
  }, [tier]);

  const activeMetric = METRICS.find(m => m.key === metric) || METRICS[0];
  const MetaIcon = meta.icon;

  // Re-sort tiles whenever the metric switch changes. Composite view =
  // Chowder desc (income-quality overlay). Yield vs History = YTB desc
  // (biggest premium vs own 3y first). Sector Z-Score = z30 asc
  // (most oversold first). Null values pushed to the bottom in all
  // three views so incomplete rows never anchor the top.
  const sortedTiles = useMemo(() => {
    const copy = [...tiles];
    copy.sort(activeMetric.sortFn);
    return copy;
  }, [tiles, activeMetric]);

  // Formats the per-tile secondary line under the ticker.
  const tileSecondary = (t) => {
    if (metric === 'composite') return t.chowder == null ? null : t.chowder.toFixed(1);
    if (metric === 'ytb') return t.ytb == null ? null : `${t.ytb > 0 ? '+' : ''}${(t.ytb * 100).toFixed(0)}%`;
    if (metric === 'z') return t.z30 == null ? null : `${t.z30 > 0 ? '+' : ''}${t.z30.toFixed(1)}\u03C3`;
    return null;
  };

  return (
    <div className="min-h-screen bg-background" data-testid={`heatmap-page-${tier}`}>
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-0">
        <div className="flex items-center gap-2 mb-1">
          <BackOrHomeButton testid={`heatmap-${tier}-back-btn`} showEscHint={false} />
          <MetaIcon className="w-5 h-5 text-amber-500" />
          <h1 className="text-xl sm:text-2xl font-black tracking-tight">{meta.title}</h1>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Squares are colored by quantitative undervaluation — tap any square for the full score breakdown.
          {computedAt && <> Scores computed {new Date(computedAt).toLocaleString()}.</>}
          {' '}<Link to={meta.challenge} className="underline underline-offset-2 hover:text-foreground" data-testid={`heatmap-to-challenge-${tier}`}>{meta.label} Challenge →</Link>
        </p>

        {/* Metric toggle */}
        <div className="flex items-center gap-1.5 flex-wrap mb-1" data-testid="heatmap-metric-toggle">
          {METRICS.map(m => (
            <button key={m.key} onClick={() => setMetric(m.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${metric === m.key ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
              data-testid={`heatmap-metric-${m.key}`}>
              {m.label}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground mb-3"><AbbrText text={activeMetric.blurb} /></p>

        {/* Legend */}
        <div className="flex items-center gap-2 mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          <AbbrTip term="HEATMAP_OVERVALUED_TRAP"><span data-testid="heatmap-legend-trap">Trap / Overvalued</span></AbbrTip>
          <div className="h-2.5 flex-1 max-w-[220px] rounded-full" style={{ background: 'linear-gradient(to right, rgb(165,0,38), rgb(100,108,122), rgb(0,104,55))' }} />
          <AbbrTip term="HEATMAP_UNDERVALUED"><span data-testid="heatmap-legend-undervalued">Undervalued</span></AbbrTip>
        </div>

        {/* Sort-order explainer — swaps with the active metric. On the
            Composite view, exposes the Chowder Rule with a tooltip that
            links to the glossary entry in a new tab. */}
        <div className="flex items-center gap-2 mb-3 text-[11px] flex-wrap" data-testid="heatmap-sort-explainer">
          <span className="font-semibold text-foreground">Sorted by:</span>
          <span className="text-muted-foreground">{activeMetric.sortLabel}</span>
          {metric === 'composite' && (
            /* Feb 2026 — Wrap in AbbrTip so users get the same 1-sentence
               popover + "Read full definition →" affordance every other
               link on the heatmap pages uses, instead of jumping straight
               to the glossary. The Glossary anchor is preserved inside
               AbbrTip's own footer link. */
            <AbbrTip
              term="CHOWDER"
              className="!inline-flex items-center gap-1 !decoration-muted-foreground/60 hover:!decoration-foreground text-muted-foreground hover:text-primary"
            >
              <span
                className="inline-flex items-center gap-1"
                data-testid="heatmap-chowder-glossary-link"
              >
                <HelpCircle className="w-3 h-3" /> What&apos;s Chowder?
              </span>
            </AbbrTip>
          )}
          {metric === 'composite' && (
            <span className="inline-flex items-center gap-1.5 ml-auto text-[10px]" data-testid="heatmap-chowder-key">
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/40 font-bold">PASS</span>
              <span className="text-muted-foreground">meets Chowder threshold</span>
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-zinc-500/15 text-zinc-600 dark:text-zinc-400 border border-zinc-500/40 font-bold ml-1">MISS</span>
              <span className="text-muted-foreground">below threshold</span>
            </span>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-1.5">
            {Array.from({ length: 40 }).map((_, i) => <div key={i} className="aspect-square rounded-md bg-muted animate-pulse" />)}
          </div>
        ) : (
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-1.5" data-testid="heatmap-grid">
            {sortedTiles.map(t => {
              const secondary = tileSecondary(t);
              const distressed = metric === 'ytb' && (t.flags || []).includes('distressed_yield');
              const chow = metric === 'composite' ? chowderVerdict(t.chowder, t.dividend_yield) : null;
              return (
                <button key={t.ticker} onClick={() => openTile(t.ticker)}
                  className={`relative aspect-square rounded-md flex flex-col items-center justify-center text-white font-black tracking-tight hover:scale-105 hover:z-10 hover:shadow-lg transition-transform ${distressed ? 'ring-2 ring-amber-400 ring-offset-1 ring-offset-background' : ''}`}
                  style={{ backgroundColor: heatColor(t.color?.[metric]) }}
                  data-testid={`heatmap-tile-${t.ticker}`}>
                  <span className="text-[10px] sm:text-xs leading-none">{t.ticker}</span>
                  {secondary && (
                    <span className="text-[9px] sm:text-[10px] font-bold opacity-95 mt-0.5 leading-none tabular-nums" data-testid={`heatmap-tile-value-${t.ticker}`}>
                      {secondary}
                    </span>
                  )}
                  {chow && (
                    <span
                      className={`absolute top-0.5 right-0.5 text-[7px] sm:text-[8px] font-black leading-none px-1 py-0.5 rounded-full ${chow.pass ? 'bg-emerald-500/90 text-white' : 'bg-black/40 text-white/90'}`}
                      title={`Chowder ${t.chowder?.toFixed(1)} vs threshold ${chow.threshold} (yield ${t.dividend_yield?.toFixed(1)}%)`}
                      data-testid={`heatmap-chowder-badge-${t.ticker}`}
                    >
                      {chow.pass ? 'PASS' : 'MISS'}
                    </span>
                  )}
                  {distressed && (
                    <span className="absolute top-0.5 left-0.5 text-[8px]" title="Distressed yield: TTM yield >=2.5x its own 3-year mean. Not a bargain." data-testid={`heatmap-distress-flag-${t.ticker}`}>
                      {'\u26A0'}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
        {!loading && tiles.length === 0 && (
          <p className="text-sm text-muted-foreground py-10 text-center">Heatmap scores are being computed — check back shortly.</p>
        )}

        <p className="text-[11px] text-muted-foreground mt-5 leading-relaxed max-w-3xl">
          Methodology: composite blends the stock&apos;s current TTM yield vs its own 3-year mean (40%), a beta-adjusted
          30-day dislocation vs its SPDR sector ETF (35%), and free-cash-flow dividend coverage (25%). Dividends
          uncovered by FCF or yields stretched ≥2.5× their own history are hard-locked to trap red regardless of
          other scores. Scores refresh nightly.
        </p>

        {/* Round P107 (Feb 2026) — Educational-screen note styled to
            match the SiteDisclaimer red-border box directly below.
            Same border color (red-400 / red-700) and text color
            (amber-900 / yellow-400) so the two read as a single unit
            of "informational, not advice" cautionary content. */}
        <div
          className="mt-3 rounded-lg border-2 border-red-400 dark:border-red-700 px-4 py-2.5 max-w-3xl"
          data-testid="heatmap-educational-note"
        >
          <p className="text-xs font-bold leading-relaxed text-amber-900 dark:text-yellow-400 text-left">
            Educational screen — not investment advice.
          </p>
        </div>
      </div>
      <SiteDisclaimer />
      <TileModal tile={selected} metric={metric} onClose={() => { setSelected(null); navigate(location.pathname, { replace: true }); }} onOpenStockModal={openStockModal} />
      <StockModal
        stock={stockData}
        open={stockModalOpen}
        onClose={() => { setStockModalOpen(false); setStockData(null); }}
        loading={loadingDetail}
        isInWatchlist={stockData ? watchlist.includes(stockData.ticker) : false}
        onToggleWatchlist={toggleWatchlist}
        isDark={isDark}
        user={user}
        onLoginRequired={() => setLoginModalOpen(true)}
        onPositionsChanged={() => { window.dispatchEvent(new Event('portfolio-changed')); }}
        onViewPortfolio={() => { setStockModalOpen(false); navigate('/?view=portfolio'); }}
        initialTab="dividend-info"
        initialChartPeriod={null}
        onCompareClick={(t) => { if (t) { setCompareTicker(t); setCompareOpen(true); } }}
        onSelectStock={openStockModal}
        watchlist={watchlist}
      />
      <CompareModal
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        initialTickerA={compareTicker}
        watchlist={watchlist}
        onTickerClick={() => {}}
      />
      <LoginModal open={loginModalOpen} onClose={() => setLoginModalOpen(false)} />
    </div>
  );
}
